import { Component, DestroyRef, ElementRef, inject, Input, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { LoadingService } from '../../services/loading.service';
import { DatePipe, NgFor, NgIf } from '@angular/common';
import { NgSelectModule } from '@ng-select/ng-select';
import { debounceTime, filter, Subject, Subscription, switchMap, tap } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { NgxToastrService } from '../../_services/ngx-toastr/ngx-toastr.service';
import { HttpServiceService } from '../../services/http-service.service';
import { Offcanvas } from 'bootstrap';
import { APP_CONSTANTS, USER_PERMISSIONS } from '../../app.constants';
import { LocalStorageService } from '../../services/local-storage.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';


@Component({
  selector: 'app-property-lease',
  imports: [ReactiveFormsModule, NgFor, NgIf,  NgSelectModule, DatePipe],
  templateUrl: './property-lease.component.html',
  styleUrl: './property-lease.component.css'
})
export class PropertyLeaseComponent {
  add_lease : boolean = false;
  addLeaseForm!: FormGroup;  
  loadingService = inject(LoadingService);
  private addLeaseSubscription?: Subscription;
  private routeSubscription? : Subscription;
  private getLeaseDetailsSubscription? : Subscription;
  private getAllTenantsSubscription?: Subscription;
  property_leases : any =  [];
  allTenants : any = [];
  propertyId: string = '';
  isEditLease : boolean = false;
  view_lease : boolean = false;
  private getAllLeasesSubscription? : Subscription;
  private getLeaseDocumentSubscription? : Subscription;
  private renewLeaseSubscription? : Subscription;
  private archiveLeaseSubscription? : Subscription;
  viewLeaseDetails : any;
  currentLease : boolean = true;
  currentLeaseList : any = [];
  previousLeaseList : any = [];
  page: any = 1;
  totalRows: number = 20;
  totalPage: any = 0;
  @ViewChild('addLeaseButton') offcanvas!: ElementRef;
   private offcanvasInstance: Offcanvas | null = null;
   searchInput$ = new Subject<string>();
   offset_limit : number = 20;
   hasMore : boolean = true;
   lastSearchTerm :any;
   private authService = inject(LocalStorageService);
   private destroyRef = inject(DestroyRef);

  constructor(private fb : FormBuilder, private route: ActivatedRoute, private alertService: NgxToastrService, private httpService: HttpServiceService){
     this.routeSubscription = this.route.paramMap.subscribe(params => {
      this.propertyId = params.get('id') ?? "";      
    });
    this.addLeaseForm = this.fb.group({
      lease_amount : ['', [Validators.required]],
      security_deposit : ['', [Validators.required]],
      agreement_date : ['', [Validators.required]],
      in_date :[''],
      out_date : [{ value: '', disabled: true }],
      start_date : ['', [Validators.required]],
      end_date : [{ value: '', disabled: true }, [Validators.required]],
      leaving_reason : [''],
      yearly_increment : [''],
      lease_document : [null, [this.fileRequiredValidator]],
      tenants : ['', [Validators.required]],
      uid : [''],
      document_url : ['']
    })
  }

  ngOnInit(): void {
    this.addLeaseForm.get('in_date')?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      const outDateControl = this.addLeaseForm.get('out_date');
      if (value) {
        outDateControl?.enable();
      } else {
        outDateControl?.disable();
      }
    });
    this.addLeaseForm.get('start_date')?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      const endDateControl = this.addLeaseForm.get('end_date');
      if (value) {
        endDateControl?.enable();
      } else {
        endDateControl?.disable();
      }
    });
    this.getAllPropertyLeases();
    if(this.checkAddLease) this.getAllUsers();

    this.searchInput$
          .pipe(
            debounceTime(300),
            takeUntilDestroyed(this.destroyRef),
            filter(term => {        
            const skip = term === this.lastSearchTerm;
            return !skip;
          }),
            tap((term) => {
              this.page = 1;
              this.hasMore = true;
              this.lastSearchTerm = term;
            }),
            switchMap(term =>{
              let query = `&search=${term}&role_identifier=tenant`;
              return this.httpService.getAllUsers(this.page, this.offset_limit, query);
            }
            )
          )
          .subscribe(results => {
            this.allTenants = results.data["users"] ?? [];
            if(this.allTenants.length >= results.data["total"]){
              this.hasMore = false;
            }
            this.allTenants = results.data.users?.map((user: { first_name: string; last_name: string; }) => ({
              ...user,
              fullName: `${user.first_name} ${user.last_name}`
            }));
          });
  }

  ngAfterViewInit() {
      setTimeout(() => {
        const offcanvasElement = this.offcanvas?.nativeElement;
        if (offcanvasElement) {
          this.offcanvasInstance = Offcanvas.getOrCreateInstance(offcanvasElement, {
            backdrop: 'static', 
            keyboard: false,
          });        
        }  
      });
  }

  addLeaseToggle(){
    this.add_lease = !this.add_lease;
  }

  addLeaseModal(){
    this.addLeaseToggle();
    if (this.offcanvasInstance) {
      this.offcanvasInstance.show();
    }
  }

  addLeaseClose(){
    this.addLeaseToggle();
    this.isEditLease = false;
    this.addLeaseForm.reset();    
    if (this.offcanvasInstance) {
      this.offcanvasInstance.hide();
    }
  }

  addLease(){
    if(!this.addLeaseForm.valid) return;
    const formData = new FormData();
    let uid : string = '';
    const propertyLeaseObj = Object.fromEntries(
      Object.entries(this.addLeaseForm.value).filter(([_, v]) => v !== null && v !== '')
    ) as unknown as any;
    if(!propertyLeaseObj?.lease_document?.["name"]) delete propertyLeaseObj?.lease_document;
    Object.keys(propertyLeaseObj).forEach((key) => { 
      let value = propertyLeaseObj[key];
      if (Array.isArray(value)) {
        value.forEach(item => {
          formData.append(`${key}[]`, item);
        });
      } else {
        if(key == 'security_deposit') value = parseInt(value);
        if(key == 'uid'){
           uid = value;
        }else{
          if(key !='document_url') formData.append(key, value);
        }
      } 
    });
    if(this.isEditLease){      
      this.addLeaseSubscription = this.httpService.editLease(formData, this.propertyId, uid).subscribe({
          next : (response) => {
            this.getAllPropertyLeases();
            this.addLeaseClose();
            this.alertService.success(response.message, "toast-top-right");
          }
        });
      }else{
        this.addLeaseSubscription = this.httpService.addLease(formData, this.propertyId).subscribe({
          next : (response) => {
            this.getAllPropertyLeases();
            this.addLeaseClose();
            this.addLeaseForm.reset();
            this.alertService.success(response.message, "toast-top-right");
          }
        });
    }
  }

  renewLease(uid : string){
    this.renewLeaseSubscription = this.httpService.renewLease(uid, this.propertyId).subscribe({
      next : (response) =>{
        this.getAllPropertyLeases();
        this.alertService.success(response.message, "toast-top-right");
      }
    })
  }

  archiveLease(uid : string){
    this.archiveLeaseSubscription = this.httpService.archiveLease(uid, this.propertyId).subscribe({
      next : (response) =>{
        this.getAllPropertyLeases();
        this.alertService.success(response.message, "toast-top-right");
      }
    })
  }

  onImageChange(event: any) {
    const file = event.target.files[0];
    if (file) {
      //const reader = new FileReader();
      //reader.onload = () => {
        //this.selectedImage = reader.result; // Store preview image
      //};
      //reader.readAsDataURL(file);

      this.addLeaseForm.patchValue({ lease_document: file ?? "" });
      this.addLeaseForm.get('lease_document')?.updateValueAndValidity();
    }
  }

  viewLease(uid : string){
    this.getLeaseDetailsSubscription = this.httpService.getLeaseDetailsById(uid, this.propertyId).subscribe({
      next : (response) => {
        let selectedTenants = [];
        let allTenantsList = [...this.allTenants];
        for(let user of response.data?.["tenants"]){
            selectedTenants.push(user.uid);
            let userFound = allTenantsList.find((tenant : any) => tenant.uid == user.uid);
            if(!userFound){
              allTenantsList.push({
                ...user,
                fullName: `${user.first_name} ${user.last_name}`})
              }
            }
        this.allTenants = [...allTenantsList];
        this.isEditLease = true;
        this.addLeaseForm.patchValue({
          ...response.data,
          tenants : selectedTenants,
          // lease_document : response.data["document_url"] ? response.data["document_url"] : ""
        });
        this.addLeaseForm.get('lease_document')?.clearValidators();
        this.addLeaseForm.get('lease_document')?.updateValueAndValidity();
        this.addLeaseModal();
      }
    })
  }

  viewImage(imageUrl : string){
    this.getLeaseDocumentSubscription = this.httpService.getBillImages(imageUrl).subscribe({
      next : (response) => {
        window.open(response.data["url"] , "_blank");
      }
    })
  }

  getAllPropertyLeases(){
    this.getAllLeasesSubscription = this.httpService.getAllLeases(this.propertyId).subscribe({
      next : (response) => {        
          this.currentLeaseList = response.data["active"];
          this.previousLeaseList = response.data["archived"];
          this.property_leases = JSON.parse(JSON.stringify(this.currentLeaseList));
      }
    })
  }

  changeLeases(){
    this.currentLease = !this.currentLease;
    if (this.currentLease) {
      this.property_leases = JSON.parse(JSON.stringify(this.currentLeaseList));
    }
    else {
      this.property_leases = JSON.parse(JSON.stringify(this.previousLeaseList));
    }
  }

  getAllUsers(){
    let query = `&role_identifier=${APP_CONSTANTS.USER_ROLES.TENANT}`;
    this.getAllTenantsSubscription = this.httpService.getAllUsers(this.page, this.offset_limit, query).subscribe({
      next : (response) => {
        let users = response.data.users?.length ? response.data.users : [];
        this.allTenants = users?.map((user: { first_name: string; last_name: string; }) => ({
          ...user,
          fullName: `${user.first_name} ${user.last_name}`
        }));
      },
      error : (error) => {
        console.log(error);
      }
    });
  }

   onScrollToEnd() {
    // Optional: For pagination / infinite scroll
    if (!this.hasMore) return;
    this.page+=1;
    //this.offset_limit+=20;
    let query = "&role_identifier=tenant";    
    this.httpService.getAllUsers(this.page, this.offset_limit, query).subscribe(response => {
      let resp = response.data["users"] ?? [];
      if(this.allTenants.length >= response.data["total"]){
          this.hasMore = false;
      }
      resp = resp?.map((user: { first_name: string; last_name: string; }) => ({
          ...user,
          fullName: `${user.first_name} ${user.last_name}`
      }));
      this.allTenants = [...this.allTenants, ...resp];      
    });
  }

  viewLeaseToggle(document : any){
    this.view_lease = !this.view_lease;
    if(this.view_lease && document) this.viewLeaseDetails = document;
  }

  fileRequiredValidator(control: AbstractControl) {
    return control.value ? null : { fileRequired: true };
  }

  ngOnDestroy(){
    this.addLeaseSubscription?.unsubscribe();
    this.routeSubscription?.unsubscribe();
    this.getAllLeasesSubscription?.unsubscribe();
    this.getLeaseDetailsSubscription?.unsubscribe();
    this.getAllTenantsSubscription?.unsubscribe();
    this.getLeaseDocumentSubscription?.unsubscribe();
    this.renewLeaseSubscription?.unsubscribe();
    this.archiveLeaseSubscription?.unsubscribe();
  }

  get checkAddLease() {
    return this.authService.checkPermission(USER_PERMISSIONS.PROPERTIES.ADD_LEASE);
  }

  get checkArchiveLease() {
    return this.authService.checkPermission(USER_PERMISSIONS.PROPERTIES.ARCHIVE_LEASE);
  }

  get checkPreviousLease() {
    return this.authService.checkPermission(USER_PERMISSIONS.PROPERTIES.PREVIOUS_LEASES);
  }

  get checkViewLease() {
    return this.authService.checkPermission(USER_PERMISSIONS.PROPERTIES.VIEW_LEASE);
  }

  get checkEditLease() {
    return this.authService.checkPermission(USER_PERMISSIONS.PROPERTIES.EDIT_LEASE);
  }

  get checkRenewLease() {
    return this.authService.checkPermission(USER_PERMISSIONS.PROPERTIES.RENEW_LEASE);
  }

}
